/**
 * Etiquetas UI para guideDesign.source (transparencia de pipeline).
 */

export type GuideDesignSource =
  | 'material_designer'
  | 'briefing_sections'
  | 'legacy_blueprint'
  | string;

export interface GuideDesignMutation {
  enforcer: string;
  blockId: string | null;
  changeType: string;
  reason: string;
}

export interface GuideDesignMeta {
  source: GuideDesignSource;
  designerAttempted?: boolean;
  designerOk?: boolean;
  degradations?: string[];
  /** Ajustes post-LLM (enforcers/composers). Vacío si no hubo. */
  mutations?: GuideDesignMutation[];
}

export type GuideDesignBadgeTone = 'artifice' | 'research' | 'legacy' | 'session';

export interface GuideDesignBadgeView {
  label: string;
  tone: GuideDesignBadgeTone;
  tooltip: string;
}

const DEGRADATION_HINTS: Record<string, string> = {
  adapter_failed: 'El artífice respondió, pero no se pudo adaptar al formato Sala (adapter_failed).',
  designer_no_structure:
    'El artífice no devolvió una estructura usable (designer_no_structure).',
  designer_agent_not_configured: 'Agente artífice no configurado en el servidor.',
};

function formatDegradation(code: string): string {
  const key = String(code || '').trim();
  return DEGRADATION_HINTS[key] || `Motivo: ${key}`;
}

export function resolveGuideDesignBadge(meta: GuideDesignMeta | null | undefined): GuideDesignBadgeView | null {
  if (!meta?.source) return null;
  const source = String(meta.source).trim();
  const degradations = Array.isArray(meta.degradations)
    ? meta.degradations.map(String).filter(Boolean)
    : [];
  const degradationNote = degradations.length
    ? degradations.map(formatDegradation).join(' · ')
    : '';

  if (source === 'material_designer') {
    return {
      label: 'Diseño: Artífice IA',
      tone: 'artifice',
      tooltip: degradationNote
        ? `Estructura del agente diseñador. ${degradationNote}`
        : 'Estructura propuesta por el agente diseñador de material (Artífice).',
    };
  }

  if (source === 'briefing_sections') {
    return {
      label: 'Diseño: Investigación curricular',
      tone: 'research',
      tooltip: degradationNote
        ? `Arco desde el briefing de investigación. ${degradationNote}`
        : 'Arco armado desde las secciones del briefing de investigación curricular.',
    };
  }

  if (
    source.startsWith('session_flow') ||
    source === 'oa20_measurement_inquiry' ||
    source === 'session_flow_project' ||
    source === 'session_flow_workshop' ||
    source === 'session_flow_source_inquiry'
  ) {
    const mutationNote =
      Array.isArray(meta.mutations) && meta.mutations.length
        ? ` · Post-LLM: ${meta.mutations.length} ajuste(s)`
        : '';
    return {
      label: 'Diseño: Flujo de sesión',
      tone: 'session',
      tooltip: degradationNote
        ? `Blueprint desde el plan de sesión. ${degradationNote}${mutationNote}`
        : `Blueprint derivado del flujo/plan de la sesión vinculada.${mutationNote}`,
    };
  }

  // legacy_blueprint y restantes → plantilla base (invito a revisar/regenerar)
  return {
    label: 'Diseño: Plantilla base',
    tone: 'legacy',
    tooltip: degradationNote
      ? `Plantilla curricular base — conviene revisar o regenerar. ${degradationNote}`
      : 'Plantilla curricular base (sin artífice ni briefing de secciones). Conviene revisar o regenerar si el arco se siente genérico.',
  };
}
